import {
  CreateProductSessionShownEventInput,
  ProductSessionGroup,
  SelectedOption,
  SelectedSessionWithVariant,
  SelectedVariants,
  UserBookingSelections,
  ClickEventHandlerInput,
  CreateProductSessionSelectedEventInput,
} from '@/types'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNotification } from '@/hooks/NotificationHook'
import { format, parse } from 'date-fns'
import { AvailabilityFeedback, Price, PriceWrapper } from './BookingAvailablityNew.styles'
import { createCompositeKey } from '@/helpers/AnalyticsHelper'
import {
  AUS_CALL_TO_BOOK_NUMBER,
  AUS_CALL_TO_BOOK_TEXT,
  NZL_CALL_TO_BOOK_TEXT,
  NZL_CALL_TO_BOOK_NUMBER,
} from '@/constant/segment'
import { formatTimeWithAmPm } from '@/helpers/BookingHelper'
import { logger } from '@/logging/Logger'
import BookingStepsContainer from '@/components/BookingStepsContainer'
import { scrollToElement } from '@/helpers/PageScrollHelper'
import {
  Spacing,
  BookingDateScroller,
  BookingSessionSelector,
  Body,
  AvailabilityLoader,
} from '@big-red-group/storefront-common-ui'
import {
  formatCurrency,
  useProductAvailability,
  calculateSessionPriceWithCertificates,
} from '@big-red-group/storefront-common-checkout'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { getUrlPrefix } from '@/helpers/UrlHelper'
import {
  OptionsType,
  ProductAvailabilityRequest,
  ProductAvailabilityResponse,
  ProductAvailabilitySession,
  ProductOption,
  ProductSession,
} from '@big-red-group/storefront-common-checkout'
import SelectedSessionSummary from '@/components/SelectedSessionSummary'
import { addDays, getNoAvailabilityMessage } from '@/helpers/BookingAvailabilityHelper'
import { BookingCalendarDate } from '@big-red-group/storefront-common-checkout/dist/types'
import { BookingContext } from '@/context/BookingContext'
import InfoBadge from './InfoBadge'
import { centsToDollars } from '@big-red-group/storefront-common-checkout'
import { JsendResponse } from '@big-red-group/storefront-common-checkout/dist/types'

const log = logger()

export type BookingAvailabilityProps = {
  handlerKey: string | undefined
  productId: number
  sessions: ProductSession[]
  enableBooking: (isBookingEnabled: boolean) => void
  onChange: (selectedVariants: SelectedVariants, selectedOptions: SelectedOption[]) => void
  addMetadata: (k: any) => void
  addAdditionalAttributes: (k: Map<String, any>) => void
  onSessionChecked: (
    sessionFrom: Date,
    sessionTo: Date,
    bookingCalendarType?: string,
    hasHotDeal?: boolean,
    hasLastMinutePricing?: boolean
  ) => void
  onSessionShown: (input: Omit<CreateProductSessionShownEventInput, 'product' | 'url'>) => void
  onReload: boolean
  currentStep: number
  updateCurrentStep: (index: number) => void
  getSelectedVariants: () => SelectedVariants
  onUserSelection: (userSelection: UserBookingSelections) => void
  userBookingSelections: UserBookingSelections
  onLoadingAvailability: (loadingAvailability: boolean) => void
  productSessionGroups?: ProductSessionGroup[]
  bookingDate?: Date
  setBookingDate: (date: Date) => void
  dateSummary: string
  checkAvailabilityFlag: boolean
  bookingCalendar: BookingCalendarDate[]
  onSessionSelected?: (input: Omit<CreateProductSessionSelectedEventInput, 'product' | 'url'>) => void
  onLinkClick?: (params: ClickEventHandlerInput) => void
}

export type ProductSessionGroupItem = {
  name?: string
  description?: string
  groupMinPrice: number
  groupMinRetailPrice: number
  sessions: ProductAvailabilitySession[]
}
const BookingAvailabilityNew: React.FC<BookingAvailabilityProps> = ({
  handlerKey,
  productId,
  sessions,
  enableBooking,
  onChange,
  addMetadata,
  addAdditionalAttributes,
  onSessionChecked,
  onSessionShown,
  onReload,
  currentStep,
  updateCurrentStep,
  getSelectedVariants,
  userBookingSelections,
  onUserSelection,
  onLoadingAvailability,
  productSessionGroups,
  bookingDate,
  setBookingDate,
  dateSummary,
  checkAvailabilityFlag,
  bookingCalendar,
  onLinkClick,
  onSessionSelected,
}) => {
  const notification = useNotification()
  const defaultProperties = useDefaultProperties()
  const agentHandlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const baseUrl = getUrlPrefix(agentHandlerKey)
  const productAvailability = useProductAvailability(baseUrl)
  const { useRedemptionLayout, certificates, appliedEvs } = useContext(BookingContext)

  // todo: this should be smarter in the future when we have more than 2 currencies
  // Also, agent configuration should be contain the region code.
  const callToBookPhoneNumber =
    defaultProperties?.agentConfiguration?.currencyCode === 'NZD' ? NZL_CALL_TO_BOOK_NUMBER : AUS_CALL_TO_BOOK_NUMBER

  const callToBookPhoneNumberText =
    defaultProperties?.agentConfiguration?.currencyCode === 'NZD' ? NZL_CALL_TO_BOOK_TEXT : AUS_CALL_TO_BOOK_TEXT

  const [loadingAvailability, setLoadingAvailability] = useState(false)
  const [selectSessionTriggered, setSelectSessionTriggered] = useState(false)
  const [hasSelectedOption, setHasSelectedOption] = useState(false)
  const [selectedDisplayDate, setSelectedDisplayDate] = useState<Date>()
  const [hasProductSessions, setHasProductSessions] = useState(false)
  const [hasAdjacentAvailability, setHasAdjacentAvailability] = useState(false)
  const [serverError, setServerError] = useState(false)
  const [noSessionError, setNoSessionError] = useState(false)
  const [productSessions, setProductSessions] = useState<ProductAvailabilitySession[]>()
  const [productSessionGroupsMap, setProductSessionGroupsMap] = useState<Map<String, ProductSessionGroupItem>>(
    new Map()
  )
  const [showSessionStep, setShowSessionStep] = useState(false)
  const [getSessionMap, setSessionMap] = useState<Map<number, ProductSession>>(new Map())
  const [getProductOptionMap, setProductOptionMap] = useState<Map<number, ProductOption>>(new Map())
  const [selectedSessionId, setSelectedSessionId] = useState<number | string | undefined>(
    userBookingSelections?.previouslySelectedSession
  )
  const [selectedGroupId, setSelectedGroupId] = useState(userBookingSelections?.previouslySelectedGroupId)
  const prevBookingDateRef = useRef<Date | undefined>()

  // Move to lib, used to transform LOKI availability metadata -> TAPI (currently used only fareharbour)
  const transformMetadata = (metadataArray?: { key: string; value: string }[]): Record<string, string> => {
    if (!metadataArray) return {}

    return metadataArray.reduce<Record<string, string>>((acc, { key, value }) => {
      acc[key] = value
      return acc
    }, {})
  }

  useEffect(() => {
    setHasSelectedOption(
      userBookingSelections?.previouslySelectedOptions?.some((option) => option.quantity > 0) ?? false
    )
  }, [userBookingSelections?.previouslySelectedOptions])

  useEffect(() => {
    if (!hasSelectedOption) {
      return
    }
    setShowSessionStep(true)
    setNoSessionError(false)
    setHasProductSessions(false)
    setProductSessions([])
    enableBooking(false)
    setSelectedSessionId(undefined)
    checkAvailability(false, bookingDate)
  }, [checkAvailabilityFlag])

  useEffect(() => {
    const { sessionMap, optionMap } = sessions.reduce<{
      sessionMap: Map<number, ProductSession>
      optionMap: Map<number, ProductOption>
    }>(
      ({ sessionMap, optionMap }, session) => {
        sessionMap.set(session.id, session)

        session.productOptions.forEach((option) => {
          optionMap.set(option.id, option)
        })

        return { sessionMap, optionMap }
      },
      { sessionMap: new Map(), optionMap: new Map() }
    )

    setSessionMap(sessionMap)
    setProductOptionMap(optionMap)
  }, [sessions])

  useEffect(() => {
    setBookingDate(bookingDate!)
    setNoSessionError(false)
    setSelectedDisplayDate(undefined)
    setHasProductSessions(false)
    setProductSessions([])
  }, [onReload])

  useEffect(() => {
    if (getSessionMap.size === 0) return

    if (userBookingSelections?.previouslySelectedDate && !selectSessionTriggered) {
      setBookingDate(userBookingSelections?.previouslySelectedDate)
      checkAvailability(false, userBookingSelections?.previouslySelectedDate)
      return
    }
  }, [getSessionMap])

  // Trigger availability reset on booking date change
  useEffect(() => {
    const getNormalisedDate = (date?: Date) => {
      const normalizedDate = new Date(date || Date.now())
      normalizedDate.setHours(0, 0, 0, 0)
      return normalizedDate
    }

    const currentBookingDate = getNormalisedDate(bookingDate)
    const prevBookingDate = prevBookingDateRef.current ? getNormalisedDate(prevBookingDateRef.current) : null

    if (!prevBookingDate || prevBookingDate.getTime() !== currentBookingDate.getTime()) {
      if (hasProductSessions || selectedSessionId) {
        resetAvailability()
      }
      prevBookingDateRef.current = currentBookingDate
    }
  }, [bookingDate])

  useEffect(() => {
    if (selectedSessionId == undefined || selectedDisplayDate == undefined) {
      return
    }
    const selectedSession = productSessions?.find(
      (session) =>
        (session.id || session.sessionKey) === selectedSessionId &&
        session.sessionDate === format(selectedDisplayDate, 'yyyy-MM-dd')
    )
    if (['AVAILABLE', 'PUBLIC_CALL'].includes(selectedSession?.status!)) {
      handleAvailableBooking()
    }
  }, [selectedSessionId, selectedDisplayDate])

  const formatBookingDate = (date: Date) => format(date ?? new Date(), 'yyyy-MM-dd')

  const checkAvailability = async (checkAdjacentAvailability: boolean, date?: Date) => {
    try {
      if (!userBookingSelections?.previouslySelectedOptions) {
        return
      }
      scrollToElement('availability')
      onLoadingAvailability(true)
      setNoSessionError(false)
      setLoadingAvailability(true)
      setHasProductSessions(false)
      setServerError(false)

      const productAvailabilitySelectedOptions: OptionsType[] = userBookingSelections?.previouslySelectedOptions?.map(
        ({ type, name, quantity, allIds }) => ({
          type,
          name,
          quantity,
          ids: allIds,
        })
      )

      const selectedBookingDate = checkAdjacentAvailability
        ? addDays(bookingDate!, 1)
        : (date ?? bookingDate ?? new Date())

      const selectedDate = formatBookingDate(selectedBookingDate)

      const toDate = checkAdjacentAvailability ? addDays(selectedBookingDate!, 2) : selectedBookingDate!

      if (checkAvailabilityFlag) {
        // Consume data for Product Session Checked event
        const booking = bookingCalendar.find((entry) => entry.date === selectedDate)
        const hasHotDeal = booking?.hasHotDeal ?? false
        const hasLastMinutePricing = Boolean(
          booking?.lastMinutePricing &&
            Object.values(booking.lastMinutePricing).some((value) => value !== undefined && value !== null)
        )
        onSessionChecked(selectedBookingDate, toDate, 'Scroller', hasHotDeal, hasLastMinutePricing)
      }

      const productAvailabilityRequest: ProductAvailabilityRequest = {
        productId: productId,
        optionTypes: productAvailabilitySelectedOptions,
        from: selectedDate!,
        to: formatBookingDate(toDate!),
        pickup: userBookingSelections?.previouslySelectedPickup,
      }

      const productAvailabilityResponse: JsendResponse<ProductAvailabilityResponse> = await productAvailability(
        productAvailabilityRequest,
        handlerKey
      )
      if (productAvailabilityResponse.status === 'success') {
        const { liveAvailability } = productAvailabilityResponse.data

        // Flatten all sessions across dates
        const productAvailabilitySessions = liveAvailability.dates.flatMap(
          ({ date, sessions: availabilitySessions }) => {
            // Separate sessions with IDs vs without IDs
            const sessionsWithIds = availabilitySessions.filter(
              (session): session is ProductAvailabilitySession & { id: number } => Boolean(session.id)
            )
            const sessionsWithoutIds = availabilitySessions.filter(
              (session): session is ProductAvailabilitySession => !session.id
            )

            // Sort sessions by existing session IDs
            const sortedSessionsWithIds =
              availabilitySessions
                ?.filter((originalSession) => sessionsWithIds.some((session) => session.id === originalSession.id))
                ?.map((originalSession) => sessionsWithIds.find((session) => session.id === originalSession.id)!) ?? []

            const orderedSessions = [...sortedSessionsWithIds, ...sessionsWithoutIds]

            const certificateIds = new Set<number>()

            orderedSessions
              .filter((session) => session.status === 'AVAILABLE')
              .forEach((session) => {
                session.options?.forEach((option) => {
                  option.certificateRef?.forEach((certId) => {
                    certificateIds.add(certId)
                  })
                })
              })

            const appliedEvs = liveAvailability?.certificates?.filter((cert) => certificateIds.has(cert.id))

            return orderedSessions?.map((session) => {
              const currentSession = session.id ? getSessionMap.get(session.id) : sessions?.at(0)
              // if handlerKey === 'ibis2' get tourtime from session.metadata
              const tourTime =
                handlerKey === 'ibis2'
                  ? // @ts-ignore
                    session.metadata?.find((meta) => meta.key === 'tourTime')?.value || session.tourTime
                  : session.tourTime

              return {
                ...session,
                sessionKey:
                  handlerKey === 'ibis2'
                    ? Array.isArray(session.metadata)
                      ? session.metadata.find((meta) => meta.key === 'sessionKey')?.value
                      : undefined
                    : undefined,
                sessionName: currentSession
                  ? session.tourTime === undefined
                    ? currentSession.sessionName
                    : `${currentSession.sessionName} @ ${session.tourTime}`
                  : session.tourTime || session.sessionName,
                sessionDate: date,
                sessionGroupId: currentSession?.sessionGroupId?.toString(),
                sessionTime: currentSession
                  ? tourTime || formatTimeWithAmPm(currentSession.tourHour!, currentSession.tourMinute)
                  : tourTime,
                hotDeal: currentSession?.hotDeal?.message,
                hasPickups: currentSession?.hasPickups,
                isExtra: currentSession?.isExtra,
                tourHour: currentSession?.tourHour,
                tourMinute: currentSession?.tourMinute,
                tourDuration: currentSession?.tourDuration,
                metadata: session.metadata,
                productOptions: currentSession?.productOptions?.map((productOption) => {
                  // Check if this product option exists in the ordered session
                  const matchingOption = session.options?.find((option) => option.id === productOption.id)

                  if (matchingOption?.certificateRef?.length) {
                    // If this product option has a certRef, attach appliedEvs
                    return {
                      ...productOption,
                      appliedEvs: appliedEvs?.filter((ev) => matchingOption.certificateRef?.includes(ev.id)),
                    }
                  }

                  return productOption
                }),
              }
            })
          }
        )

        // Get the first available session
        const firstAvailableSession = productAvailabilitySessions.find(({ status }) =>
          ['AVAILABLE', 'PUBLIC_CALL'].includes(status)
        )

        setNoSessionError(!firstAvailableSession)
        setHasAdjacentAvailability(checkAdjacentAvailability)
        if (firstAvailableSession?.sessionDate) {
          setSelectedDisplayDate(parse(firstAvailableSession.sessionDate, 'yyyy-MM-dd', new Date()))
        }

        // Build session groups
        const sessionsGroup = new Map<string, ProductSessionGroupItem>()

        productAvailabilitySessions.forEach((availableSession) => {
          // Normalise all prices
          availableSession.totalPrice = centsToDollars(availableSession.totalPrice)
          availableSession.totalRetailPrice = centsToDollars(availableSession.totalRetailPrice)
          availableSession.totalSellPrice = centsToDollars(availableSession.totalSellPrice)

          const sessionKey = Array.isArray(availableSession.metadata)
            ? availableSession.metadata.find((meta) => meta.key === 'sessionKey')?.value
            : undefined

          const id = availableSession.id || sessionKey || ''
          const groupId =
            handlerKey === 'ibis2'
              ? 'IBIS_GROUP' // Single group for all ibis sessions
              : availableSession.sessionGroupId?.toString() || `NO_GROUP${id}`
          const existingSessionsInGroup = sessionsGroup.get(`${groupId}${availableSession.sessionDate}`)
          const sessionGroup = productSessionGroups?.find((psg) => psg.id.toString() === groupId.toString())

          const hasLowerPrice =
            !existingSessionsInGroup?.groupMinPrice ||
            availableSession.totalPrice < existingSessionsInGroup?.groupMinPrice!

          sessionsGroup.set(`${groupId}${availableSession.sessionDate}`, {
            name: sessionGroup?.name || availableSession.sessionName,
            description: sessionGroup?.description,
            sessions: [...(existingSessionsInGroup?.sessions || []), availableSession],
            groupMinPrice: hasLowerPrice
              ? availableSession.totalPrice
              : existingSessionsInGroup?.groupMinPrice || availableSession.totalPrice,
            groupMinRetailPrice: hasLowerPrice
              ? availableSession.totalRetailPrice
              : existingSessionsInGroup?.groupMinRetailPrice || availableSession.totalRetailPrice,
          })
        })
        setProductSessionGroupsMap(sessionsGroup)
        setProductSessions(productAvailabilitySessions)
        setHasProductSessions(true)

        onSessionShown({
          checkedSessionDate: selectedBookingDate!,
          productAllSessions: productAvailabilitySessions,
        })
      }

      if (!productAvailabilityResponse.data) {
        setServerError(true)
      }
    } catch (e) {
      setServerError(true)
      log.error('Error checking booking availability', {}, e as Error)
    }

    onLoadingAvailability(false)
    setLoadingAvailability(false)
  }

  //TODO - Refactor this function
  const handleAvailableBooking = async () => {
    try {
      const productAvailabilitySession = productSessions?.find(
        (pas) =>
          (pas.id || pas.sessionKey) === selectedSessionId &&
          (pas.sessionGroupId === selectedGroupId || pas.sessionDate === formatBookingDate(selectedDisplayDate!))
      )
      setSelectSessionTriggered(true)
      if (!productAvailabilitySession || productAvailabilitySession.status === 'PUBLIC_CALL') {
        return
      }
      let selectedVariants: Record<string, SelectedSessionWithVariant> = {}
      let currentSession = sessions?.at(0)!
      const newSelectedOptions = userBookingSelections?.previouslySelectedOptions?.map((selectedOption) => {
        const option = productAvailabilitySession.options.find((option) => selectedOption?.allIds?.includes(option.id))
        currentSession = getSessionMap.get(productAvailabilitySession.id) || currentSession
        if (option) {
          const matchingProductOption = productAvailabilitySession.productOptions?.find(
            (productOption) => productOption.id === option.id
          )

          let appliedEvs: SelectedOption['appliedEvs'] = []
          // TODO: Add types to lib
          // @ts-ignore
          if (option.certificateRef?.length && matchingProductOption?.appliedEvs) {
            // @ts-ignore
            appliedEvs = matchingProductOption.appliedEvs.filter((ev) => option.certificateRef?.includes(ev.id))
          }

          const updatedSelectedOption: SelectedOption = {
            ...selectedOption,
            appliedEvs,
            id: option.id,
            price: option.price,
            productMetaData:
              handlerKey === 'ibis2'
                ? { departureKey: productAvailabilitySession.sessionKey }
                : // @ts-ignore
                  transformMetadata(productAvailabilitySession.metadata),
            metadata: {
              ...selectedOption.metadata,
              productOptionName: getProductOptionMap.get(option.id)!.name,
              productSessionName: productAvailabilitySession.sessionName!,
              productSessionId: productAvailabilitySession.id,
              productSessionHour: currentSession?.tourHour,
              productSessionMinute: currentSession?.tourMinute,
              productSessionDuration: currentSession?.tourDuration,
            },
          }

          const variantKey = createCompositeKey(productAvailabilitySession.id, option.id)
          selectedVariants[variantKey] = {
            ...currentSession,
            option: getProductOptionMap.get(option.id)!,
            quantity: updatedSelectedOption.quantity,
          }

          return updatedSelectedOption
        }

        return selectedOption
      })
      onChange(selectedVariants, newSelectedOptions ?? [])
      onUserSelection({
        previouslySelectedOptions: newSelectedOptions,
        previouslySelectedSession: productAvailabilitySession.id,
        previouslySelectedDate: parse(productAvailabilitySession.sessionDate!, 'yyyy-MM-dd', new Date()),
        resetPickupLocation: userBookingSelections?.previouslySelectedSession !== productAvailabilitySession.id,
        previouslySelectedGroupId: selectedGroupId,
        // @ts-ignore
        appliedEvs: productAvailabilitySession.appliedEvs,
        subTotal: productAvailabilitySession.totalSellPrice,
      })

      addMetadata({
        ...productAvailabilitySession.metadata,
        // @ts-ignore - lib types need to be updated to reflect
        availablityId: productAvailabilitySession.metadata?.find((item) => item.key === 'availabilityId')?.value,
        sessionId: productAvailabilitySession.id,
        sessionGroupId: currentSession.sessionGroupId,
        departureKey: productAvailabilitySession.sessionKey,
      })
      setBookingDate(parse(productAvailabilitySession.sessionDate!, 'yyyy-MM-dd', new Date()))
      addAdditionalAttributes(new Map().set('bookingDate', productAvailabilitySession.sessionDate!))
      enableBooking(true)

      onSessionSelected?.({
        checkedSessionDate: bookingDate!,
        selectedSession: productAvailabilitySession,
      })

      // PLAT-1512: Scroll to the availability section after selecting a session
      // A delay using setTimeout was needed because selecting a session causes layout shifts due to dynamic content updates
      setTimeout(() => {
        scrollToElement('availability')
      }, 300)
    } catch (e) {
      console.log(e)
      log.error('Error booking', {}, e as Error)

      // TODO review message
      notification('Unable to process booking. Try again later.', 'Booking', 'error')
    }
  }

  const hasBookingDateSelected = hasSelectedOption && !(dateSummary === undefined || dateSummary === '')

  const getSessionSummary = (): React.ReactNode => {
    if (!selectedSessionId || !selectedDisplayDate) {
      return ''
    }

    const productSession = productSessions?.find(
      (session) =>
        (session.id || session.sessionKey) === selectedSessionId &&
        session.sessionDate === format(selectedDisplayDate, 'yyyy-MM-dd')
    )

    if (!productSession?.sessionName || !userBookingSelections.previouslySelectedOptions?.length) {
      return ''
    }

    return (
      <SelectedSessionSummary
        sessionName={productSession.sessionName}
        selectedOptions={userBookingSelections.previouslySelectedOptions}
      />
    )
  }

  const sessionSummary: React.ReactNode = useMemo(() => getSessionSummary(), [selectedSessionId, userBookingSelections])

  const resetAvailability = () => {
    enableBooking(false)
    setNoSessionError(false)
    setSelectedSessionId(undefined)
    setSelectedGroupId(undefined)
    checkAvailability(false, bookingDate)
  }

  const selectSession = (
    id: number,
    sessionKey: string | number | undefined,
    groupId: string,
    totalPrice: number,
    isCallToBook: boolean
  ) => {
    if (isCallToBook) {
      window.location.href = `tel:${callToBookPhoneNumber}`
      return
    }
    setSelectedSessionId(id || sessionKey)
    setSelectedGroupId(groupId?.toString())
  }

  return (
    <div id="availability">
      {showSessionStep && (
        <BookingStepsContainer
          index={3}
          currentStep={currentStep}
          updateCurrentStep={updateCurrentStep}
          isDisabled={!hasBookingDateSelected || !hasSelectedOption}
          title="3. Select package"
          onReset={() => resetAvailability()}
          summary={sessionSummary}
          onLinkClick={onLinkClick}
        >
          {useRedemptionLayout && (
            <>
              <InfoBadge
                heading="Choose your preferred package option"
                subText="Your voucher covers the original cost, but if you'd like to
                select a premium option or time, we'll show any extra costs before you book."
                iconName="gift"
                backgroundColor="#e9ebff"
                borderColor="#394BF1"
                textColor="brand-secondary"
              />
              <Spacing />
            </>
          )}
          {bookingDate && (
            <BookingDateScroller
              bookingCalendar={bookingCalendar}
              bookingDate={bookingDate}
              setBookingDate={(newDate) => {
                setBookingDate(newDate)
                checkAvailability(false, newDate)
              }}
              showPricing={!useRedemptionLayout}
            />
          )}
          <Spacing />
          {serverError && (
            <div>
              We&apos;re unable to check availability for this experience right now. Give us a call on{' '}
              <a href="tel:{callToBookPhoneNumber}">{callToBookPhoneNumber}</a>
            </div>
          )}
          {noSessionError ? (
            <AvailabilityFeedback>{getNoAvailabilityMessage(bookingDate!)}</AvailabilityFeedback>
          ) : (
            <>
              {loadingAvailability && (
                <>
                  <AvailabilityLoader />
                </>
              )}
              {hasProductSessions && (
                <>
                  <BookingSessionSelector
                    productSessionGroupsMap={productSessionGroupsMap}
                    selectedDisplayDate={selectedDisplayDate}
                    selectSession={selectSession}
                    callToBookText={callToBookPhoneNumberText}
                    formatCurrency={formatCurrency}
                    numSessionGroups={productSessionGroups?.length ?? 1}
                    loading={loadingAvailability}
                    showPricing={!useRedemptionLayout}
                  />
                </>
              )}
            </>
          )}
        </BookingStepsContainer>
      )}
    </div>
  )
}

export default BookingAvailabilityNew
